import { useCallback } from 'react';
import { useThemeContext } from 'contexts';
import { captureLastSearch } from 'pages/api/last-search/lastSearchApiClient';

import type { ListingParams } from 'contexts/preferences/listing-params/types';

/**
 * Hook to capture and store the user's last search parameters
 *
 * @returns A callback function that takes a userId and listing parameters to store the last search.
 *          It resolves once the request has settled (errors are swallowed by the API client).
 * @example
 * ```tsx
 * const captureLastSearch = useCaptureLastSearch();
 * captureLastSearch(user.id, listingParams);
 * ```
 */
export const useCaptureLastSearch = () => {
  const { themeName } = useThemeContext();

  return useCallback(async (userId: string | number, lastSearch: ListingParams): Promise<void> => {
    if (userId && userId !== null && lastSearch) {
      await captureLastSearch({ userId, lastSearch, sourceTheme: themeName });
    }
  }, [themeName]);
};